/* SystemsFooter — clean, responsive, dark-gradient footer mirroring the
 * Academy pattern: brand block spanning 2 cols, 2 link columns, then a
 * thin compliance strip at the bottom. All hrefs are real (no placeholder
 * `#`) so the footer is fully active. Phone routes the visitor straight to
 * Systems' direct line. */
import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Linkedin, Facebook, Instagram, Youtube, Phone, Globe } from 'lucide-react';
import ZheltaLogo from './ZheltaLogo';
import DivisionBridge from './DivisionBridge';

export default function SystemsFooter() {
  return (
    <footer
      className="text-zinc-800 bg-[#F5F5F7]"
      data-testid="systems-footer"
    >
      <section className="max-w-[1200px] mx-auto px-4 lg:px-6 py-12 grid grid-cols-1 md:grid-cols-4 gap-10">
        {/* Brand block — spans 2 cols on md+ */}
        <div className="md:col-span-2">
          <Link to="/systems" className="inline-flex items-center gap-3 mb-4" data-testid="systems-footer-brand">
            <ZheltaLogo size={36} />
            <div className="leading-none">
              <div className="text-[18px] font-extrabold tracking-[0.2em]">ZHELTA</div>
              <div className="text-[10px] font-semibold tracking-[0.3em] text-[#f5a623]">SYSTEMS</div>
            </div>
          </Link>
          <p className="text-[14px] text-zinc-600 leading-relaxed max-w-[480px]">
            Enterprise-grade IT, security, and cloud operations for organisations that can't afford downtime — strategy, deployment, and 24/7 support, vendor-neutral by design.
          </p>

          <ul className="mt-5 flex flex-col sm:flex-row sm:flex-wrap gap-x-6 gap-y-2 text-zinc-500 text-[13px]">
            <li>
              <a
                href="tel:+19142000609"
                data-testid="systems-footer-phone"
                className="inline-flex items-center gap-1.5 hover:text-[#c9a227]"
              >
                <Phone className="w-3.5 h-3.5 text-[#c9a227]" /> 914-200-0609
              </a>
            </li>
            <li>
              <a
                href="mailto:sales@zhelta.com"
                data-testid="systems-footer-email"
                className="inline-flex items-center gap-1.5 hover:text-[#c9a227]"
              >
                <Mail className="w-3.5 h-3.5 text-[#c9a227]" /> sales@zhelta.com
              </a>
            </li>
            <li className="inline-flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-[#c9a227]" /> New York, NY · USA
            </li>
          </ul>
        </div>

        {/* Solutions */}
        <div>
          <h4 className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#f5a623] mb-3">Solutions</h4>
          <ul className="space-y-2 text-[13.5px] text-zinc-700">
            <li><a href="/systems#solutions"  className="hover:text-[#c9a227]">Managed IT services</a></li>
            <li><a href="/systems#solutions"  className="hover:text-[#c9a227]">Cloud &amp; infrastructure</a></li>
            <li><a href="/systems#solutions"  className="hover:text-[#c9a227]">Cybersecurity support</a></li>
            <li><a href="/systems#solutions"  className="hover:text-[#c9a227]">Help desk &amp; remote support</a></li>
            <li><a href="/systems#compare"    className="hover:text-[#c9a227]">Compare plans</a></li>
          </ul>
        </div>

        {/* Company */}
        <div>
          <h4 className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#f5a623] mb-3">Company</h4>
          <ul className="space-y-2 text-[13.5px] text-zinc-700">
            <li><Link to="/systems"               className="hover:text-[#c9a227]">Systems home</Link></li>
            <li><a href="/systems#why"            className="hover:text-[#c9a227]">Why ZHELTA</a></li>
            <li><a href="/systems#expertise"      className="hover:text-[#c9a227]">Our expertise</a></li>
            <li>
              <a
                href="https://portal.zhelta.com/p/scheduler/KfMLXSkC6pkS4utFn"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#c9a227]"
                data-testid="systems-footer-book-demo"
              >
                Book a demo
              </a>
            </li>
            <li><Link to="/systems/contact"       className="hover:text-[#c9a227]">Contact sales</Link></li>
          </ul>
        </div>
      </section>

      {/* Bridge to other divisions */}
      <DivisionBridge current="systems" />

      {/* Compliance strip */}
      <section className="border-t border-zinc-200">
        <div className="max-w-[1200px] mx-auto px-4 lg:px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="text-[12px] text-zinc-500">
            <Link
              to="/copyright"
              data-testid="systems-footer-copyright"
              className="hover:text-[#c9a227] transition"
              title="Copyright & Terms of Use"
            >
              © {new Date().getFullYear()} ZHELTA LLC. All rights reserved. ZHELTA Systems is a division of ZHELTA LLC.
            </Link>
          </div>
          <div className="flex items-center gap-4 text-zinc-500">
            <Link
              to="/privacy"
              data-testid="systems-footer-privacy"
              className="text-[11.5px] hover:text-[#c9a227]"
            >
              Privacy policy
            </Link>
            <Link
              to="/cookies"
              data-testid="systems-footer-cookies"
              className="text-[11.5px] hover:text-[#c9a227]"
            >
              Cookies
            </Link>
            <a
              href="https://www.linkedin.com/company/zhelta-llc/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              data-testid="systems-footer-linkedin"
              className="hover:text-[#c9a227]"
            >
              <Linkedin className="w-4 h-4" />
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61562259287313"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              data-testid="systems-footer-facebook"
              className="hover:text-[#c9a227]"
            >
              <Facebook className="w-4 h-4" />
            </a>
            <a
              href="https://www.instagram.com/zhelta_support?igsh=dTdqbGo4MWZqcW1z&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              data-testid="systems-footer-instagram"
              className="hover:text-[#c9a227]"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <a
              href="https://youtube.com/@zheltallc?si=x-hOD-nWZpWempMK"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
              data-testid="systems-footer-youtube"
              className="hover:text-[#c9a227]"
            >
              <Youtube className="w-4 h-4" />
            </a>
            <Link
              to="/"
              className="text-[11.5px] hover:text-[#c9a227] inline-flex items-center gap-1"
              data-testid="systems-footer-mainsite"
            >
              <Globe className="w-3 h-3" /> ZHELTA main site
            </Link>
          </div>
        </div>
      </section>
    </footer>
  );
}
